import React from 'react'
import { useTranslation } from 'react-i18next'
import { AddressEntityType, BuildingType, ObjectType } from 'types/ApiResponse'
import {
  StyledDataColumn,
  StyledDataContainer,
  StyledDataProperty,
  StyledHeader,
  StyledLine,
  StyledPropertyLabel,
  StyledPropertyValue,
  StyledSectionContainer,
} from '../PropertyInfo.styled'
import checkMarkYes from 'assets/images/icons/checkMarkYes.svg'
import checkMarkNo from 'assets/images/icons/checkMarkNo.svg'

const PropertyBasicInfo = (props: any) => {
  const { propertyData }: { propertyData: AddressEntityType } = props
  const { t } = useTranslation()
  const objectType = propertyData?.propertyDetails?.objectType
  const buildingType = propertyData?.propertyDetails?.buildingType

  const getBooleanValue = (value?: boolean) => {
    return (
      <>
        {value ? (
          <img src={checkMarkYes} alt="checkMarkYes" />
        ) : value === false ? (
          <img src={checkMarkNo} alt="checkMarkNo" />
        ) : null}
        {value ? t('common.yes') : value === null ? '—' : value === false ? t('common.no') : '—'}
      </>
    )
  }

  if (objectType === ObjectType.PLOT_OF_LAND) {
    return (
      <StyledSectionContainer style={{ paddingTop: 0 }}>
        <StyledHeader>{t('newAddressModal.objectDataTab.headers.basic_information')}</StyledHeader>
        <StyledDataContainer>
          <StyledDataColumn>
            <StyledDataProperty>
              <StyledPropertyLabel>
                {t('newAddressModal.objectDataTab.plotArea')}
              </StyledPropertyLabel>
              <StyledPropertyValue>
                {propertyData?.propertyDetails?.plotArea
                  ? `${propertyData?.propertyDetails?.plotArea} m²`
                  : '—'}
              </StyledPropertyValue>
            </StyledDataProperty>
          </StyledDataColumn>
          <StyledDataColumn />
        </StyledDataContainer>
      </StyledSectionContainer>
    )
  }

  return (
    <StyledSectionContainer style={{ paddingTop: 0 }}>
      <StyledHeader>{t('newAddressModal.objectDataTab.headers.basic_information')}</StyledHeader>
      <StyledDataContainer>
        <StyledDataColumn>
          <StyledDataProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.houseType')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {propertyData?.propertyDetails?.houseOrApartmentType
                ? t(
                    `houseOrApartmentType.${propertyData?.propertyDetails?.houseOrApartmentType?.toLowerCase()}`
                  )
                : '—'}
            </StyledPropertyValue>
          </StyledDataProperty>
          <StyledDataProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.buildYear')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {propertyData?.propertyDetails?.constructionYear
                ? propertyData?.propertyDetails?.constructionYear
                : '—'}
            </StyledPropertyValue>
          </StyledDataProperty>
          <StyledDataProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.livingArea')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {propertyData?.propertyDetails?.livingArea
                ? `${propertyData?.propertyDetails?.livingArea} m²`
                : '—'}
            </StyledPropertyValue>
          </StyledDataProperty>
          {buildingType === BuildingType.RESIDENTIAL_AND_COMMERCIAL && (
            <StyledDataProperty>
              <StyledPropertyLabel>
                {t('newAddressModal.objectDataTab.commercialArea')}
              </StyledPropertyLabel>
              <StyledPropertyValue>
                {propertyData?.propertyDetails?.commercialArea
                  ? `${propertyData?.propertyDetails?.commercialArea} m²`
                  : '—'}
              </StyledPropertyValue>
            </StyledDataProperty>
          )}
          <StyledDataProperty>
            <StyledPropertyLabel>{t('newAddressModal.objectDataTab.plotArea')}</StyledPropertyLabel>
            <StyledPropertyValue>
              {propertyData?.propertyDetails?.plotArea
                ? `${propertyData?.propertyDetails?.plotArea} m²`
                : '—'}
            </StyledPropertyValue>
          </StyledDataProperty>
          <StyledDataProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.numberOfFloors')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {propertyData?.propertyDetails?.numberOfFloors
                ? propertyData?.propertyDetails?.numberOfFloors
                : '—'}
            </StyledPropertyValue>
          </StyledDataProperty>
          <StyledDataProperty>
            <StyledPropertyLabel>{t('newAddressModal.objectDataTab.floor')}</StyledPropertyLabel>
            <StyledPropertyValue>
              {propertyData?.propertyDetails?.floorNumber
                ? propertyData?.propertyDetails?.floorNumber
                : '—'}
            </StyledPropertyValue>
          </StyledDataProperty>
          {objectType !== ObjectType.HOUSE && (
            <StyledDataProperty>
              <StyledPropertyLabel>
                {objectType === ObjectType.APARTMENT
                  ? t('newAddressModal.objectDataTab.numberOfUnits')
                  : t('newAddressModal.objectDataTab.numberOfResidentialUnits')}
              </StyledPropertyLabel>
              <StyledPropertyValue>
                {propertyData?.propertyDetails?.numberOfUnits
                  ? propertyData?.propertyDetails?.numberOfUnits
                  : '—'}
              </StyledPropertyValue>
            </StyledDataProperty>
          )}
          {buildingType === BuildingType.RESIDENTIAL_AND_COMMERCIAL && (
            <StyledDataProperty>
              <StyledPropertyLabel>
                {t('newAddressModal.objectDataTab.numberOfCommercialUnits')}
              </StyledPropertyLabel>
              <StyledPropertyValue>
                {propertyData?.propertyDetails?.numberOfCommercialUnits
                  ? propertyData?.propertyDetails?.numberOfCommercialUnits
                  : '—'}
              </StyledPropertyValue>
            </StyledDataProperty>
          )}
          <StyledDataProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.construction')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {propertyData?.propertyDetails?.construction
                ? t(
                    `constructionType.${propertyData?.propertyDetails?.construction?.toLowerCase()}`
                  )
                : '—'}
            </StyledPropertyValue>
          </StyledDataProperty>
        </StyledDataColumn>
        <StyledDataColumn>
          <StyledDataProperty>
            <StyledPropertyLabel>{t('newAddressModal.objectDataTab.roof')}</StyledPropertyLabel>
            <StyledPropertyValue>
              {propertyData?.propertyDetails?.roof
                ? t(`roofType.${propertyData?.propertyDetails?.roof?.toLowerCase()}`)
                : '—'}
            </StyledPropertyValue>
          </StyledDataProperty>
          <StyledDataProperty>
            <StyledPropertyLabel>{t('newAddressModal.objectDataTab.basement')}</StyledPropertyLabel>
            <StyledPropertyValue>
              {propertyData?.propertyDetails?.basement
                ? t(`basementType.${propertyData?.propertyDetails?.basement?.toLowerCase()}`)
                : '—'}
            </StyledPropertyValue>
          </StyledDataProperty>
          <StyledDataProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.basementConversion')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {propertyData?.propertyDetails?.basementConversion
                ? t(
                    `basementConversionType.${propertyData?.propertyDetails?.basementConversion?.toLowerCase()}`
                  )
                : '—'}
            </StyledPropertyValue>
          </StyledDataProperty>
          <StyledDataProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.outdoorFacilities')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {propertyData?.propertyDetails?.outdoorFacilities
                ? t(
                    `outdoorFacilitiesType.${propertyData?.propertyDetails?.outdoorFacilities?.toLowerCase()}`
                  )
                : '—'}
            </StyledPropertyValue>
          </StyledDataProperty>
          {objectType !== ObjectType.HOUSE && (
            <StyledDataProperty>
              <StyledPropertyLabel>
                {t('newAddressModal.objectDataTab.elevator')}
              </StyledPropertyLabel>
              <StyledPropertyValue>
                {getBooleanValue(propertyData?.propertyDetails?.elevator)}
              </StyledPropertyValue>
            </StyledDataProperty>
          )}
          {objectType === ObjectType.APARTMENT && (
            <StyledDataProperty>
              <StyledPropertyLabel>{t('newAddressModal.objectDataTab.garden')}</StyledPropertyLabel>
              <StyledPropertyValue>
                {getBooleanValue(propertyData?.propertyDetails?.garden)}
              </StyledPropertyValue>
            </StyledDataProperty>
          )}
          <StyledDataProperty>
            <StyledPropertyLabel>{t('newAddressModal.objectDataTab.garage')}</StyledPropertyLabel>
            <StyledPropertyValue>
              {propertyData?.propertyDetails?.garages
                ? propertyData?.propertyDetails?.garages
                : '—'}
            </StyledPropertyValue>
          </StyledDataProperty>
          <StyledDataProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.parkingPlace')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {propertyData?.propertyDetails?.outdoorParkingSpaces
                ? propertyData?.propertyDetails?.outdoorParkingSpaces
                : '—'}
            </StyledPropertyValue>
          </StyledDataProperty>
          <StyledDataProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.energyCertificate.energyClass')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {propertyData?.propertyDetails?.energyCertificate?.energyClass
                ? t(
                    `energyClassTypes.${propertyData?.propertyDetails?.energyCertificate?.energyClass.toLowerCase()}`
                  )
                : '—'}
            </StyledPropertyValue>
          </StyledDataProperty>
        </StyledDataColumn>
      </StyledDataContainer>
      <StyledLine />
    </StyledSectionContainer>
  )
}

export default PropertyBasicInfo
